/**
 * docApplicability — DAT-1/DAT-2 (Document Applicability Transparency pack)
 * + DMS-1/DMS-2 (Doc-Match Strictness hotfix).
 *
 * Doc-first projection of "why is this doc applicable?". Walks the
 * existing signals already on `SiteDocument` (intent ids from URE,
 * fulfilled ids from UPE, classifier scores from DCT) and emits a
 * unified list of `{requirementId, label, score, rationale, reasonSource}`
 * rows the row chip + lineage inspector both render.
 *
 * Three rationale tiers (sorted in this priority order so the
 * "Solves" chip strip leads with the strongest signal):
 *
 *   1. **Intent (URE)** — user explicitly uploaded this doc against a
 *      requirement via the Coverage Matrix `[Upload]` button. The most
 *      authoritative signal because the human said so.
 *   2. **Fulfilled (UPE)** — the slot-aware status engine concluded
 *      this doc fulfils the requirement after SED extraction. Implies
 *      a populated SED slot mapped to the requirement.
 *   3. **DCT match** — Document Matching Quality scored the doc above
 *      the per-report-type threshold. Reads through `doc.relevance`,
 *      so available even on docs that haven't been routed through the
 *      ranked-matcher yet.
 *
 * Pure / side-effect free for the legacy projection path. Loosens the
 * requirement-first EVL surface into the doc-first axis without re-running
 * any scorer.
 *
 * DMS-1 (2026-05-18 — Reputation-risk hotfix): a finance spreadsheet that
 * scored 0.90 against `feasibility` as a category was being projected as
 * a 90% match against EVERY feasibility requirement (Flood / Geotech /
 * Title / Zoning / …). The fix has two layers:
 *   (a) **Filename plausibility pre-filter (sync, this commit).** Tier 3
 *       rows whose filename strongly negates the requirement are
 *       suppressed before any Firestore read. Cheap, predictable, ships
 *       in the same render tick as the rest of the chip strip.
 *   (b) **DM2 per-requirement score map (DMS-2).** When the caller has
 *       pre-fetched the `docMatching` cache, Tier 3 reads per-`(doc,
 *       requirement)` scores from it instead of the per-report-type
 *       proxy + applies verification gates (no boilerplate rationale,
 *       evidence quotes present at score ≥ 0.7, confidence not 'low').
 *
 * `CLASSIFIER_VERSION` bumps whenever the strictness math changes so
 * downstream caches (notably the legacy `doc.relevance` per-report-type
 * scores) can be invalidated without re-running every scorer. Callers
 * that have cached an `ApplicableRequirement[]` keyed on the doc should
 * compose the version into their cache key.
 */
import type { SiteDocument } from '../types/siteRag';
import type { ReportTypeId } from '../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../config/reportTypes';
import { getDocumentChecklistAcross } from '../config/documentChecklists';
import type { ChecklistItem } from '../config/documentChecklists';
import { filenameStronglyNegates } from './docMatchFilenameGate';

// Re-export so existing callers continue to import from this module.
export { filenameStronglyNegates } from './docMatchFilenameGate';

/** Where the applicability claim came from — drives the chip tooltip. */
export type ApplicabilityReason = 'intent' | 'fulfilled' | 'dct-match';

/**
 * DMS-2 — single entry in the per-`(doc, requirementId)` DM2 score
 * map. Mirrors `RelevanceScore` from `relevanceScoring.ts` but
 * intentionally narrowed to the four fields Tier 3 actually consults
 * so the test fixtures can stay tiny.
 */
export interface DctScoreEntry {
  /** 0..1 — same scale as `doc.relevance` so chips render identically. */
  score: number;
  /** ≤200-char model-authored explanation naming specific evidence. */
  rationale: string;
  /** Up to 3 short verbatim quotes lifted from the doc. */
  evidenceQuotes: string[];
  /** Model self-reported confidence in its own score. */
  confidence: 'low' | 'medium' | 'high';
}

export interface ApplicableRequirement {
  /** Stable checklist row id (`ChecklistItem.id`). */
  requirementId: string;
  /** Human-readable label from the registry, or the id as a fallback. */
  label: string;
  /** 0..1 score — DCT max-across-report-types for this requirement's
   *  parent report type. 1.0 for explicit intent / fulfilled when no
   *  DCT score exists (signal-strength sentinel for sorting). */
  score: number;
  /** Why this requirement appears in the list (see `ApplicabilityReason`). */
  reasonSource: ApplicabilityReason;
  /** ≤200 char human-readable explanation, suitable for a tooltip. */
  rationale: string;
  /** Report type(s) the requirement belongs to — surfaces in the
   *  inspector table as a column hint. */
  reportTypes: ReportTypeId[];
  /** DMS-2 — verbatim quotes from the DM2 scorer when this row came
   *  from the per-requirement cache. Surfaced by DocLineageInspector
   *  so the user can verify the match against the doc. Empty / absent
   *  on the legacy per-report-type projection path. */
  evidenceQuotes?: string[];
  /** DMS-2 — DM2 self-reported confidence in the score. Absent on the
   *  legacy projection path. */
  confidence?: 'low' | 'medium' | 'high';
}

const ALL_REPORT_TYPES: ReportTypeId[] = Object.keys(REPORT_TYPE_REGISTRY) as ReportTypeId[];

/**
 * Build a `{checklistItemId → {item, reportTypes[]}}` index covering
 * every report type. Used to resolve labels for intent / fulfilled ids
 * and to find which report types each requirement belongs to.
 *
 * `getDocumentChecklistAcross` already dedupes by `item.id` across
 * types; we keep the dedup but also remember which types contained
 * the same id so the inspector can show e.g. "Phase I ESA — applies
 * to Feasibility + Environmental".
 */
function buildRequirementIndex(): Map<string, { item: ChecklistItem; reportTypes: ReportTypeId[] }> {
  const out = new Map<string, { item: ChecklistItem; reportTypes: ReportTypeId[] }>();
  for (const reportType of ALL_REPORT_TYPES) {
    for (const item of getDocumentChecklistAcross([reportType])) {
      const entry = out.get(item.id);
      if (entry) {
        if (!entry.reportTypes.includes(reportType)) entry.reportTypes.push(reportType);
      } else {
        out.set(item.id, { item, reportTypes: [reportType] });
      }
    }
  }
  return out;
}

/** Cached requirement index — checklist registry is static at build time. */
let REQUIREMENT_INDEX: Map<string, { item: ChecklistItem; reportTypes: ReportTypeId[] }> | null = null;
function getRequirementIndex(): Map<string, { item: ChecklistItem; reportTypes: ReportTypeId[] }> {
  if (REQUIREMENT_INDEX === null) REQUIREMENT_INDEX = buildRequirementIndex();
  return REQUIREMENT_INDEX;
}

/** Test-only — drop the cache so a registry reshape test sees fresh data. */
export function __resetRequirementIndexForTests(): void {
  REQUIREMENT_INDEX = null;
}

/* ── Projection helpers (exported for direct testing) ─────────── */

export const APPLICABILITY_DCT_THRESHOLD = 0.40;

/**
 * DMS-1 — strictness version. Bump whenever the Tier-3 math changes so
 * downstream callers that memoize an `ApplicableRequirement[]` per-doc
 * can compose this into their cache key and re-evaluate after a deploy.
 * Format: `YYYYMMDD-N` matching `relevanceScoring.MODEL_VERSION`.
 *
 * Bump log:
 *   - 20260518-1: initial Tier-3 filename-plausibility pre-filter +
 *     DM2-cache-aware path. Per-report-type proxy is now gated by
 *     `filenameStronglyNegates`; rows whose filename clearly belongs
 *     to a different document type are dropped before the threshold
 *     check fires.
 */
export const CLASSIFIER_VERSION = '20260518-1';

/**
 * DMS-1 — filename negative patterns live in `./docMatchFilenameGate`
 * so both the chip-render side (this file) and the DM2 scorer side
 * (`services/docMatching/relevanceScoring.ts`) can use the same
 * helper without the DM2 path having to drag `REPORT_TYPE_REGISTRY`
 * into its import graph (which broke AdminPanel's partial-mock test
 * contract).
 */

/**
 * Compute the max DCT relevance score across report types whose
 * checklist contains `requirementId`. Falls back to the unconditional
 * max relevance when the requirement maps to no known report type
 * (defensive — keeps the chip working if the checklist registry
 * drifts).
 */
function dctScoreForRequirement(
  doc: Pick<SiteDocument, 'relevance'>,
  requirementId: string,
): number {
  const entry = getRequirementIndex().get(requirementId);
  const rel = doc.relevance ?? {};
  let max = 0;
  if (entry) {
    for (const t of entry.reportTypes) {
      const v = rel[t];
      if (typeof v === 'number' && Number.isFinite(v) && v > max) max = v;
    }
  }
  if (max === 0) {
    for (const v of Object.values(rel)) {
      if (typeof v === 'number' && Number.isFinite(v) && v > max) max = v;
    }
  }
  return max;
}

function resolveLabel(requirementId: string): { label: string; reportTypes: ReportTypeId[] } {
  const entry = getRequirementIndex().get(requirementId);
  return {
    label: entry?.item.label ?? requirementId,
    reportTypes: entry?.reportTypes ?? [],
  };
}

/* ── DMS-1 telemetry ───────────────────────────────────────────── */

/**
 * DMS telemetry counters — in-process. Mirrors the
 * `relevanceScoring.getScoringTelemetry()` pattern so admin tooling can
 * surface "how often did the strictness gate fire?" without a separate
 * audit-log write path.
 */
export interface DocMatchStrictnessTelemetry {
  /** Tier-3 rows suppressed by the filename plausibility gate. */
  filenameNegativeSuppressed: number;
  /** DMS-2 strong-claim rejections (score≥0.7 but boilerplate /
   *  no quotes / low confidence). Incremented only when callers pass
   *  a `dctScoreMap`. */
  strongClaimRejected: number;
}

const telemetry: DocMatchStrictnessTelemetry = {
  filenameNegativeSuppressed: 0,
  strongClaimRejected: 0,
};

export function getDocMatchStrictnessTelemetry(): DocMatchStrictnessTelemetry {
  return { ...telemetry };
}

export function resetDocMatchStrictnessTelemetry(): void {
  telemetry.filenameNegativeSuppressed = 0;
  telemetry.strongClaimRejected = 0;
}

/* ── DMS-2 — DM2 cache loader ──────────────────────────────────── */

/**
 * DMS-2 — load the per-`(doc, requirementId)` DM2 score cache for the
 * supplied doc, scoped to every known checklist requirement.
 *
 * Returns a `Map<requirementId, DctScoreEntry>` containing only the
 * rows that actually exist in the `docMatching/{cacheKey}` collection
 * — missing rows are absent from the map. Callers then pass the map
 * to `applicableRequirements(doc, { dctScoreMap })` to get the strict
 * Tier 3 path with verification gates.
 *
 * **Cost guard.** Reads happen ONCE per dialog open — never per chip
 * render. Callers (DocLineageInspector) drive the load behind a
 * `useEffect(open)` gate so the 95% of users who never click "Show
 * detail" pay zero Firestore reads.
 *
 * Never throws — a Firestore outage degrades to an empty map (Tier 3
 * then surfaces no rows at all, which is strictly safer than the
 * legacy projection's false-positive bloom).
 *
 * Implementation: parallel `getDoc` per requirement. With ~20
 * requirements per doc, that's 20 reads per dialog open, well below
 * the 50-read budget in the pack's AUDIT step 8.
 */
export async function loadDctScoreMapForDoc(
  doc: Pick<SiteDocument, 'id' | 'contentHash' | 'fileName'>,
): Promise<Map<string, DctScoreEntry>> {
  const out = new Map<string, DctScoreEntry>();
  if (!doc.id) return out;
  // Same fallback identity as `relevanceScoring.scoreDocAgainstChecklistItem`
  // so we read the rows it wrote.
  const contentHash = doc.contentHash ?? `nohash::${doc.fileName ?? doc.id}`;
  let cacheKeyFor: (a: { docId: string; itemId: string; contentHash: string }) => string;
  let MODEL_VERSION: string;
  try {
    const mod = await import('../services/docMatching/relevanceScoring');
    cacheKeyFor = mod.cacheKeyFor;
    MODEL_VERSION = mod.MODEL_VERSION;
  } catch {
    return out;  // DM2 module unavailable in this context (test seam)
  }

  let db: unknown;
  let firestore: typeof import('firebase/firestore') | null = null;
  try {
    const fb = await import('../firebase');
    db = (fb as { db: unknown }).db;
    firestore = await import('firebase/firestore');
  } catch {
    return out;  // firebase unavailable → no cache
  }
  if (!firestore || !db) return out;
  const { doc: docRef, getDoc } = firestore;

  const requirementIds: string[] = [...getRequirementIndex().keys()];
  // Parallel-fan-out — each requirement is an independent Firestore
  // read. Aggregated through `Promise.allSettled` so a single bad row
  // doesn't kill the entire load.
  const results = await Promise.allSettled(
    requirementIds.map(async (reqId) => {
      const key = cacheKeyFor({ docId: doc.id, itemId: reqId, contentHash });
      const snap = await getDoc(docRef(db as Parameters<typeof docRef>[0], 'docMatching', key));
      if (!snap.exists()) return null;
      const data = snap.data() as Partial<DctScoreEntry> | undefined;
      if (!data) return null;
      const score = typeof data.score === 'number' && Number.isFinite(data.score) ? data.score : 0;
      const rationale = typeof data.rationale === 'string' ? data.rationale : '';
      const evidenceQuotes = Array.isArray(data.evidenceQuotes)
        ? (data.evidenceQuotes as unknown[]).filter((q): q is string => typeof q === 'string')
        : [];
      const confidenceRaw = typeof data.confidence === 'string' ? data.confidence : 'low';
      const confidence: DctScoreEntry['confidence'] =
        confidenceRaw === 'high' || confidenceRaw === 'medium' ? confidenceRaw : 'low';
      const entry: DctScoreEntry = { score, rationale, evidenceQuotes, confidence };
      return { reqId, entry };
    }),
  );
  for (const r of results) {
    if (r.status !== 'fulfilled' || !r.value) continue;
    out.set(r.value.reqId, r.value.entry);
  }
  return out;
}

/* ── Main projection ──────────────────────────────────────────── */

/**
 * Doc-first applicability list.
 *
 * Walks the three signal tiers and emits one row per requirement,
 * sorted by `reasonSource` priority (intent first, then fulfilled,
 * then dct-match) and within each tier by score desc. Deduped by
 * `requirementId` — a doc that is BOTH intended and fulfilled for
 * the same requirement appears once with the strongest reason kept.
 *
 * Below `APPLICABILITY_DCT_THRESHOLD` (0.40) DCT-match rows are
 * dropped — those rows clutter the chip strip without adding signal.
 * Intent + fulfilled signals bypass the threshold (an explicit user
 * action / a real fulfilment is signal in itself, regardless of DCT
 * score).
 *
 * Pure: same inputs → same output. The static checklist registry is
 * cached after the first call; tests calling
 * `__resetRequirementIndexForTests` get a fresh build on the next
 * invocation.
 */
export function applicableRequirements(
  doc: Pick<SiteDocument, 'relevance' | 'intendedRequirementIds' | 'fulfilledRequirementIds' | 'fileName'>,
  opts: {
    /**
     * DMS-2 — per-`(doc, requirementId)` DM2 score map. When provided,
     * Tier 3 reads from this map instead of the per-report-type proxy
     * AND applies strong-claim verification gates (boilerplate
     * rationale → reject, score≥0.7 with no evidence quotes → reject,
     * score≥0.7 with low confidence → reject). Callers that have not
     * pre-fetched DM2 cache rows omit this and get the
     * filename-pre-filtered legacy projection (still much stricter
     * than the un-filtered version, but ships in the same render tick).
     */
    dctScoreMap?: Map<string, DctScoreEntry>;
  } = {},
): ApplicableRequirement[] {
  const out = new Map<string, ApplicableRequirement>();

  // Tier 1 — Intent (URE).
  for (const reqId of doc.intendedRequirementIds ?? []) {
    const { label, reportTypes } = resolveLabel(reqId);
    const dctScore = dctScoreForRequirement(doc, reqId);
    out.set(reqId, {
      requirementId: reqId,
      label,
      // Intent rows lead the list — use dctScore when present, else
      // sentinel 1.0 so the intent row outranks any pure-DCT row.
      score: dctScore > 0 ? Math.max(dctScore, 0.5) : 1.0,
      reasonSource: 'intent',
      rationale: 'User uploaded this document against this requirement (Coverage Matrix intent).',
      reportTypes,
    });
  }

  // Tier 2 — Fulfilled (UPE). Don't overwrite an intent row; intent
  // is the more authoritative source.
  for (const reqId of doc.fulfilledRequirementIds ?? []) {
    if (out.has(reqId)) continue;
    const { label, reportTypes } = resolveLabel(reqId);
    const dctScore = dctScoreForRequirement(doc, reqId);
    out.set(reqId, {
      requirementId: reqId,
      label,
      score: dctScore > 0 ? Math.max(dctScore, 0.5) : 0.9,
      reasonSource: 'fulfilled',
      rationale: 'Document fulfilled this requirement after SED extraction (slot-aware status).',
      reportTypes,
    });
  }

  // Tier 3 — DCT match. DMS-2: prefer the per-requirement DM2 cache
  // when the caller supplied it. Falls back to the per-report-type
  // proxy when not, with the DMS-1 filename-plausibility pre-filter
  // suppressing the worst false positives in either path.
  for (const [requirementId, entry] of getRequirementIndex()) {
    if (out.has(requirementId)) continue;

    // DMS-1 — synchronous filename gate. Applies in BOTH the
    // DM2-cache path and the legacy projection path so a finance
    // workbook never claims to solve geotech/flood/title regardless
    // of which scoring axis was read.
    const effectiveFileName =
      typeof doc.fileName === 'string' && doc.fileName.trim().length > 0
        ? doc.fileName
        : typeof (doc as { name?: unknown }).name === 'string'
          ? String((doc as { name?: unknown }).name)
          : ((doc as { id?: string }).id ?? '');
    if (filenameStronglyNegates(effectiveFileName, requirementId)) {
      telemetry.filenameNegativeSuppressed += 1;
      continue;
    }

    if (opts.dctScoreMap) {
      // DMS-2 path — per-`(doc, requirementId)` DM2 cache.
      const dm2 = opts.dctScoreMap.get(requirementId);
      if (!dm2) continue;  // no per-requirement score → no chip
      if (dm2.score < APPLICABILITY_DCT_THRESHOLD) continue;

      // DMS-2 verification gates — refuse to emit a chip when the
      // claim is "strong" (≥0.7) but the supporting metadata is weak.
      const isStrong = dm2.score >= 0.7;
      const looksBoilerplate = /classifier scored this doc.*against this requirement/i.test(dm2.rationale);
      const hasQuotes = (dm2.evidenceQuotes?.length ?? 0) > 0;
      const lowConfidenceOnStrong = isStrong && dm2.confidence === 'low';

      if (isStrong && (looksBoilerplate || !hasQuotes || lowConfidenceOnStrong)) {
        telemetry.strongClaimRejected += 1;
        continue;
      }

      out.set(requirementId, {
        requirementId,
        label: entry.item.label,
        score: dm2.score,
        reasonSource: 'dct-match',
        rationale: dm2.rationale,
        evidenceQuotes: dm2.evidenceQuotes,
        confidence: dm2.confidence,
        reportTypes: entry.reportTypes,
      });
      continue;
    }

    // Legacy path — per-report-type projection. Kept for back-compat
    // callers that haven't pre-fetched the DM2 cache. The filename
    // gate above already suppressed the obvious false positives.
    const score = dctScoreForRequirement(doc, requirementId);
    if (score < APPLICABILITY_DCT_THRESHOLD) continue;
    out.set(requirementId, {
      requirementId,
      label: entry.item.label,
      score,
      reasonSource: 'dct-match',
      rationale: `Classifier scored this doc ${(score * 100).toFixed(0)}% against this requirement's report type.`,
      reportTypes: entry.reportTypes,
    });
  }

  // Sort: reasonSource priority, then score desc, then label asc.
  const priority: Record<ApplicabilityReason, number> = { intent: 0, fulfilled: 1, 'dct-match': 2 };
  return [...out.values()].sort((a, b) => {
    if (priority[a.reasonSource] !== priority[b.reasonSource]) {
      return priority[a.reasonSource] - priority[b.reasonSource];
    }
    if (b.score !== a.score) return b.score - a.score;
    return a.label.localeCompare(b.label);
  });
}
